import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  MenuItem,
  TextField,
  Button,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { useMilestones } from '../hooks/useMilestones';
import { useTimeline } from '../hooks/useTimeline';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../constants/roles';

const MilestonesPage: React.FC = () => {
  const { auth } = useAuth();
  const isChild = auth.user?.role === ROLES.CHILD;
  const [filters, setFilters] = useState<{ goalId?: string; status?: string; type?: string }>({});
  const { milestones, loading, error, reload, createMilestone, updateMilestone, completeMilestone } = useMilestones(0, filters);
  const { events, loading: eventsLoading, error: timelineError, reload: reloadTimeline } = useTimeline(0, { goalId: filters.goalId });

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<any>({ name: '', type: 'amount', targetAmount: 1000, linkedCategory: '' });
  const [draftTask, setDraftTask] = useState('');
  const [draftChecklist, setDraftChecklist] = useState<string[]>([]);
  const [rowSaving, setRowSaving] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string | null>>({});
  const [amountDrafts, setAmountDrafts] = useState<Record<string, string>>({});

  useEffect(() => { document.title = 'Milestones | Velora Money'; 
    setAmountDrafts((prev) => {
      const next = { ...prev };
      milestones.forEach((m) => {
        if (m.type === 'amount') next[m._id] = String(m.currentAmount ?? 0);
      });
      return next;
    });
  }, [milestones]);

  const grouped = useMemo(
    () => ({
      inProgress: milestones.filter((m) => m.status !== 'completed'),
      completed: milestones.filter((m) => m.status === 'completed'),
    }),
    [milestones]
  );

  const resetDraft = () => {
    setDraft({ name: '', type: 'amount', targetAmount: 1000, linkedCategory: '' });
    setDraftTask('');
    setDraftChecklist([]);
  };

  const renderProgressSubtitle = (ev: any) => {
    if (ev.kind === 'milestone_progress') {
      const threshold = Number(ev.metadata?.threshold);
      if (Number.isFinite(threshold)) return `Progress reached ${threshold}%`;
      return 'Milestone progress updated';
    }
    return null;
  };

  const withRowAction = async (id: string, action: () => Promise<void>) => {
    setRowSaving((prev) => ({ ...prev, [id]: true }));
    setRowError((prev) => ({ ...prev, [id]: null }));
    try {
      await action();
      reloadTimeline();
    } catch (e: any) {
      setRowError((prev) => ({ ...prev, [id]: e?.message || 'Failed to update progress' }));
    } finally {
      setRowSaving((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={{ xs: 2.5, md: 3 }}>
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(255, 255, 255, 0.98) 55%, rgba(46, 125, 50, 0.08) 100%)',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack spacing={1.25} sx={{ maxWidth: 760 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'rgba(25, 118, 210, 0.10)',
                color: 'primary.main',
                width: 'fit-content',
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                Progress workspace
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
              🎯 Milestones & Timeline
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Plan, track, and celebrate progress.
            </Typography>
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Overview
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                Progress Snapshot
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review what is active, what is complete, and what has recently happened in your timeline.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(3, minmax(160px, 1fr))',
                },
                gap: 2,
              }}
            >
              <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Typography variant="overline" color="text.secondary">Active</Typography>
                <Typography variant="h5" fontWeight={700}>{grouped.inProgress.length}</Typography>
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Typography variant="overline" color="text.secondary">Completed</Typography>
                <Typography variant="h5" fontWeight={700}>{grouped.completed.length}</Typography>
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Typography variant="overline" color="text.secondary">Timeline Events</Typography>
                <Typography variant="h5" fontWeight={700}>{events.length}</Typography>
              </Paper>
            </Box>
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Controls
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                Filter Milestones
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                size="small"
                label="Status"
                value={filters.status || ''}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
                sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="not_started">Not started</MenuItem>
                <MenuItem value="in_progress">In progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Type"
                value={filters.type || ''}
                onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value || undefined }))}
                sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="amount">Amount</MenuItem>
                <MenuItem value="checklist">Checklist</MenuItem>
                <MenuItem value="date">Date</MenuItem>
              </TextField>

              <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
              {!isChild && (
                <Button variant="contained" onClick={() => setOpen(true)} sx={{ width: { xs: '100%', md: 'auto' } }}>
                  + Create Milestone
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Box sx={{ flexBasis: { md: '58%' }, flexGrow: 1, minWidth: 0 }}>
            <Paper
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
              }}
            >
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                In progress
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                Active
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Track milestones that are currently moving forward.
              </Typography>
              {loading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ py: 2 }}>
                <Typography color="error.main">{error}</Typography>
                <Button size="small" sx={{ mt: 1 }} onClick={reload}>Retry</Button>
              </Box>
            ) : grouped.inProgress.length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                No active milestones yet. {isChild ? 'You can view milestones created by your household account owner.' : 'Create your first milestone to start tracking progress.'}
              </Typography>
            ) : (
              grouped.inProgress.map((m) => (
                <Paper
                  key={m._id}
                  sx={{ p: 2, my: 1, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap title={m.name}>
                        {m.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {m.type === 'amount'
                          ? `Saved $${(m.currentAmount ?? 0).toFixed(0)} / $${(m.targetAmount ?? 0).toFixed(0)}`
                          : m.type === 'checklist'
                          ? `${(m.checklist || []).filter((i) => i.done).length}/${(m.checklist || []).length} tasks`
                          : m.deadline
                          ? `Due ${new Date(m.deadline).toLocaleDateString()}`
                          : 'Date-based'}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={m.percentComplete}
                        sx={{ mt: 1, height: 8, borderRadius: 1 }}
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                        {m.linkedCategory && <Chip size="small" label={`Category: ${m.linkedCategory}`} />}
                        {m.goalId && <Chip size="small" label="Goal-linked" />}
                        <Chip size="small" label={m.status} />
                      </Stack>
                    </Box>
                    <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      {!isChild && m.type === 'amount' && (
                        <Stack spacing={1} sx={{ width: { xs: '100%', sm: 220 }, minWidth: 0 }}>
                          <TextField
                            size="small"
                            type="number"
                            label="Current"
                            fullWidth
                            value={amountDrafts[m._id] ?? String(m.currentAmount ?? 0)}
                            disabled={Boolean(m.autoProgress)}
                            onChange={(e) => setAmountDrafts((prev) => ({ ...prev, [m._id]: e.target.value }))}
                          />
                          {m.autoProgress ? (
                            <Typography variant="caption" color="text.secondary">
                              Auto-tracked from category; disable auto progress to edit.
                            </Typography>
                          ) : (
                            <Button
                              size="small"
                              disabled={Boolean(rowSaving[m._id])}
                              sx={{ width: { xs: '100%', sm: 'auto' } }}
                              onClick={() =>
                                withRowAction(m._id, async () => {
                                  const nextCurrent = Number(amountDrafts[m._id] ?? 0);
                                  await updateMilestone(m._id, { currentAmount: Number.isFinite(nextCurrent) ? nextCurrent : 0 });
                                })
                              }
                            >
                              Save progress
                            </Button>
                          )}
                        </Stack>
                      )}
                      {!isChild && (
                        <Button
                          size="small"
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                          onClick={async () => {
                            await completeMilestone(m._id);
                            reloadTimeline();
                          }}
                        >
                          Mark done
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {m.type === 'checklist' && (m.checklist || []).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {(m.checklist || []).map((item, idx) => {
                        const label = (item.text || item.label || '').trim() || `Task ${idx + 1}`;
                        return (
                          <Stack key={`${m._id}-${idx}-${label}`} direction="row" alignItems="center" spacing={1}>
                            <Checkbox
                              size="small"
                              checked={Boolean(item.done)}
                              disabled={isChild || Boolean(rowSaving[m._id])}
                              onChange={(_, checked) =>
                                withRowAction(m._id, async () => {
                                  const current = m.checklist || [];
                                  const updatedChecklist = current.map((entry, entryIdx) =>
                                    entryIdx === idx ? { ...entry, done: checked } : entry
                                  );
                                  await updateMilestone(m._id, { checklist: updatedChecklist });
                                })
                              }
                            />
                            <Typography variant="body2" color={item.done ? 'text.secondary' : 'text.primary'}>
                              {label}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Box>
                  )}
                  {rowError[m._id] && (
                    <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                      {rowError[m._id]}
                    </Typography>
                  )}
                </Paper>
              ))
            )}
          </Paper>

          <Paper
            sx={{
              p: { xs: 2, sm: 2.5 },
              mt: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
              Completed
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              Completed
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              Review finished milestones and progress already achieved.
            </Typography>
            {loading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : grouped.completed.length ? (
              grouped.completed.map((m) => (
                <Paper
                  key={m._id}
                  sx={{ p: 2, my: 1, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600}>{m.name}</Typography>
                      <LinearProgress variant="determinate" value={100} sx={{ mt: 1, height: 8, borderRadius: 1 }} />
                    </Box>
                    <Chip color="success" label="Completed" />
                  </Stack>
                </Paper>
              ))
            ) : (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                No completed milestones yet.
              </Typography>
            )}
          </Paper>
        </Box>

        <Box sx={{ flexBasis: { md: '40%' }, flexGrow: 1, minWidth: 0 }}>
          <Paper
            sx={{
              p: { xs: 2, sm: 2.5 },
              height: '100%',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
              Activity
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              See milestone progress updates and completion history in one stream.
            </Typography>
            <Box sx={{ mt: 1, maxHeight: { xs: 'none', md: 600 }, overflowY: { xs: 'visible', md: 'auto' } }}>
            {eventsLoading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : timelineError ? (
              <Box sx={{ py: 2 }}>
                <Typography color="error.main">{timelineError}</Typography>
                <Button size="small" sx={{ mt: 1 }} onClick={reloadTimeline}>Retry</Button>
              </Box>
            ) : events.length ? (
              events.map((ev) => (
                  <Box key={ev._id} sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography fontWeight={600} noWrap title={ev.title}>
                      {ev.icon ? `${ev.icon} ` : ''} {ev.title}
                    </Typography>
                    {renderProgressSubtitle(ev) && (
                      <Typography variant="body2" color="text.secondary">
                        {renderProgressSubtitle(ev)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(ev.date).toLocaleString()} {ev.kind ? `• ${ev.kind}` : ''}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  No timeline events yet.
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </Stack>

      <Dialog
        open={open && !isChild}
        onClose={() => {
          setOpen(false);
          resetDraft();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Milestone</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <TextField
              select
              label="Type"
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            >
              <MenuItem value="amount">Amount</MenuItem>
              <MenuItem value="checklist">Checklist</MenuItem>
              <MenuItem value="date">Date</MenuItem>
            </TextField>

            {draft.type === 'amount' && (
              <>
                <TextField
                  label="Target Amount"
                  type="number"
                  value={draft.targetAmount}
                  onChange={(e) => setDraft({ ...draft, targetAmount: Number(e.target.value) })}
                />
                <TextField
                  label="Linked Category (optional)"
                  value={draft.linkedCategory}
                  onChange={(e) => setDraft({ ...draft, linkedCategory: e.target.value })}
                />
              </>
            )}

            {draft.type === 'date' && (
              <TextField
                label="Deadline"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={draft.deadline || ''}
                onChange={(e) => setDraft({ ...draft, deadline: e.target.value })}
              />
            )}

            {draft.type === 'checklist' && (
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Task"
                    fullWidth
                    value={draftTask}
                    onChange={(e) => setDraftTask(e.target.value)}
                  />
                  <Button
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    onClick={() => {
                      const task = draftTask.trim();
                      if (!task) return;
                      setDraftChecklist((prev) => [...prev, task]);
                      setDraftTask('');
                    }}
                  >
                    Add task
                  </Button>
                </Stack>
                {draftChecklist.map((task, idx) => (
                  <Stack key={`${task}-${idx}`} direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{task}</Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setDraftChecklist((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
                {draftChecklist.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Add at least one task to create a checklist milestone.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              resetDraft();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!String(draft.name || '').trim() || (draft.type === 'checklist' && draftChecklist.length < 1)}
            onClick={async () => {
              const payload =
                draft.type === 'checklist'
                  ? { ...draft, checklist: draftChecklist.map((text) => ({ text, done: false })) }
                  : draft;
              await createMilestone(payload);
              setOpen(false);
              resetDraft();
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      </Stack>
    </Container>
  );
};

export default MilestonesPage;
